import type { ConversationEvent } from '@/lib/gupshup-client';

/** Minimum a chat message must expose to be placed on a timeline. */
export interface TimelineMessage {
  id: string;
  createdAt: string;
}

/** A chat timeline entry: a message bubble or a system/agent event row. */
export type TimelineItem<M extends TimelineMessage> =
  | { kind: 'message'; message: M }
  | { kind: 'event'; event: ConversationEvent };

export function timelineTimestamp<M extends TimelineMessage>(item: TimelineItem<M>): string {
  return item.kind === 'message' ? item.message.createdAt : item.event.createdAt;
}

/** Stable React key for a timeline entry (event ids are namespaced away from message ids). */
export function timelineKey<M extends TimelineMessage>(item: TimelineItem<M>): string {
  return item.kind === 'message' ? item.message.id : `event-${item.event.id}`;
}

/**
 * Interleave messages with conversation events by timestamp. On a timestamp tie the message
 * renders first; ids break the remaining ties so the order stays stable across polls.
 *
 * Events are dropped entirely while there are no messages, and — when `hideEventsBefore` is set to
 * the oldest loaded message timestamp (i.e. older message pages exist but aren't loaded yet) —
 * older events are hidden so they never float detached above the pagination sentinel.
 */
export function buildTimeline<M extends TimelineMessage>(
  messages: M[],
  events: ConversationEvent[],
  options?: { hideEventsBefore?: string | null },
): TimelineItem<M>[] {
  const items: TimelineItem<M>[] = messages.map((message) => ({ kind: 'message', message }));

  if (events.length > 0 && messages.length > 0) {
    const cutoff = options?.hideEventsBefore ? new Date(options.hideEventsBefore).getTime() : null;

    for (const event of events) {
      if (cutoff !== null && new Date(event.createdAt).getTime() < cutoff) continue;
      items.push({ kind: 'event', event });
    }
  }

  return items.sort((a, b) => {
    const timeDiff = new Date(timelineTimestamp(a)).getTime() - new Date(timelineTimestamp(b)).getTime();
    if (timeDiff !== 0) return timeDiff;
    if (a.kind !== b.kind) return a.kind === 'message' ? -1 : 1;
    const idA = a.kind === 'message' ? a.message.id : a.event.id;
    const idB = b.kind === 'message' ? b.message.id : b.event.id;
    return idA.localeCompare(idB);
  });
}
